<template>
  <!-- "Allen noticed" — change-based findings (each on its own clock via `when`).
       Default: a speech balloon (Home). BARE: a lean labelled list for surfaces
       that already introduce Allen once (the Today briefing) — no second avatar. -->
  <div v-if="findings.length && bare" class="noticed-bare">
    <div class="noticed-bare-label"><i class="bi bi-flag"></i> {{ label || $t('today.noticed.alsoFlagged') }}</div>
    <ul class="home-findings">
      <li v-for="f in findings" :key="f.id" :class="['finding', `sev-${f.severity}`, f.accent ? `accent-${f.accent}` : '', f.icon ? 'has-glyph' : '']">
        <i v-if="f.icon" class="finding-glyph bi" :class="f.icon" aria-hidden="true"></i>
        <div class="finding-body">
          <span class="finding-title">{{ f.title }}</span>
          <span class="finding-detail">{{ f.detail }}</span>
          <span v-if="f.when" class="finding-when"><i class="bi bi-arrow-repeat"></i>{{ f.when }}</span>
        </div>
      </li>
    </ul>
  </div>

  <div v-else-if="findings.length" class="noticed-card">
    <div class="noticed-avatar" aria-hidden="true">
      <i class="bi bi-robot"></i>
      <span class="bell-badge"><i class="bi bi-bell-fill"></i></span>
    </div>
    <div class="noticed-bubble">
      <div class="noticed-head">
        <span class="noticed-name">{{ $t('today.noticed.name') }}</span>
        <span class="noticed-role">{{ $t('today.noticed.role') }}</span>
      </div>
      <ul class="home-findings">
        <li v-for="f in findings" :key="f.id" :class="['finding', `sev-${f.severity}`, f.accent ? `accent-${f.accent}` : '', f.icon ? 'has-glyph' : '']">
          <i v-if="f.icon" class="finding-glyph bi" :class="f.icon" aria-hidden="true"></i>
          <div class="finding-body">
            <span class="finding-title">{{ f.title }}</span>
            <span class="finding-detail">{{ f.detail }}</span>
            <span v-if="f.when" class="finding-when"><i class="bi bi-arrow-repeat"></i>{{ f.when }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
defineProps({
  findings: { type: Array, default: () => [] },
  // Lean list without the balloon chrome (Allen already introduced on the surface).
  bare: { type: Boolean, default: false },
  // Heading of the bare list. Empty → "Allen also flagged" in the reader's language.
  label: { type: String, default: '' },
})
</script>

<style scoped>
/* Bare (Today briefing) — a lean labelled list, no avatar/bubble. */
.noticed-bare { display: block; }
.noticed-bare-label {
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--stora-muted, #6b7a86);
  display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
}
.noticed-bare-label .bi { font-size: 0.8rem; color: #8fa2b7; }

.noticed-card { display: flex; align-items: flex-start; gap: 0.9rem; }
.noticed-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 46px; height: 46px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff;
  font-size: 1.4rem;
  box-shadow: 0 4px 10px rgba(79, 126, 179, 0.35);
}
.noticed-avatar .bell-badge {
  position: absolute;
  right: -2px; bottom: -2px;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--stora-border, #e6edf3);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px;
  color: var(--stora-accent, #4F7EB3);
}
.noticed-bubble {
  position: relative;
  flex: 1 1 auto;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  padding: 1rem 1.25rem 1.1rem;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.noticed-bubble::before {
  content: '';
  position: absolute;
  top: 18px; left: -8px;
  width: 14px; height: 14px;
  background: var(--stora-card, #fff);
  border-left: 1px solid var(--stora-border, #e6edf3);
  border-bottom: 1px solid var(--stora-border, #e6edf3);
  transform: rotate(45deg);
}
.noticed-head { display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 0.7rem; }
.noticed-name { font-weight: 700; color: var(--stora-accent, #4F7EB3); }
.noticed-role { font-size: 0.8rem; color: var(--stora-muted, #6b7a86); }

.home-findings { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.finding {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  border-left: 3px solid #8fa2b7;
  background: #f7f9fc;
  border-radius: 0 8px 8px 0;
  padding: 0.5rem 0.55rem 0.5rem 0.6rem;
}
.finding-body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.finding-title { font-weight: 600; font-size: 0.84rem; color: #1d2b3a; line-height: 1.3; }
.finding-detail { font-size: 0.78rem; color: #51637a; line-height: 1.4; }
.finding.sev-info    { border-left-color: #62B5D9; background: #f4f8fc; }
.finding.sev-warning { border-left-color: #D3A741; background: #fff8ec; }
.finding.sev-high    { border-left-color: #C21500; background: #fff3f1; }
.finding.sev-high .finding-title { color: #9a2727; }

/* Optional glyph per finding — pinned TOP-RIGHT as a corner accent so every card's
   text stays left-aligned (a leading icon would shift only the flagged card). */
.finding { position: relative; }
.finding.has-glyph { padding-right: 1.9rem; }
.finding-glyph { position: absolute; top: 7px; right: 9px; font-size: 1rem; color: #8fa2b7; }

/* Weather accent — a distinct teal/sky treatment so a weather heads-up pops out of
   the feed without being a warning (context, not an alarm) and without borrowing
   green (which reads as 'good' in this platform). */
.finding.accent-weather { border-left-color: #1f9d9d; background: linear-gradient(90deg, #ecf8f7, #f5faf2); }
.finding.accent-weather .finding-glyph { color: #178f8f; }
.finding.accent-weather .finding-title { color: #12615f; }

/* Per-finding timeframe — each signal is on its own clock. */
.finding-when {
  align-self: flex-start;
  margin-top: 0.3rem;
  font-size: 0.68rem;
  color: var(--stora-muted, #6b7a86);
  background: #eef2f6;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  display: inline-flex; align-items: center; gap: 0.28rem;
}
.finding-when i { font-size: 0.62rem; }
</style>
